import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Event: a calendar event (title, date, start time, end time or duration, time zone, location, details).
// Saved as a bare block placeholder with its settings as data-event-* attributes; Newsletters::RenderBlocks
// renders the card, and Newsletters::EventCalendar builds the .ics file (attached to the email and behind
// "Add to calendar") on the server. An event is one day: date ("2026-03-05"), start time ("19:00") and
// either an end time or a duration in minutes, all local wall time in the block's own IANA time zone.
// Blocks saved earlier carry data-event-starts-at / -ends-at ("2026-03-05T19:00"); the server still reads
// them, and the editor converts them to the date and times the first time the block is edited.
// The editor chrome lives in dashboard/shared/tiptap/_event_editor.html.erb and its preview comes from the
// same server component. The block shows one view at a time (editor or preview, see block_views.js); an
// event with a title and start opens in its preview.

const PREVIEW_DELAY_MS = 300;

const FIELDS = {
  title: 'eventTitle',
  date: 'eventDate',
  'start-time': 'eventStartTime',
  'end-time': 'eventEndTime',
  'time-zone': 'eventTimeZone',
  location: 'eventLocation',
  description: 'eventDescription',
};

// The block's attrs with a legacy start/end turned into the date and times (an end on another day is
// dropped: events are one day).
export function withDateAndTimes(attrs) {
  if (attrs.eventDate || !attrs.eventStartsAt) return attrs;
  const [date, startTime = ''] = attrs.eventStartsAt.split('T');
  const [endDate, endTime = ''] = (attrs.eventEndsAt || '').split('T');
  return {
    ...attrs,
    eventDate: date,
    eventStartTime: startTime,
    eventEndTime: endDate === date ? endTime : '',
    eventStartsAt: '',
    eventEndsAt: '',
  };
}

export const EventBlock = Node.create({
  name: 'event',

  ...blockNodeSpec('event', {
    attributes: {
      eventTitle: dataAttribute('eventTitle'),
      eventDate: dataAttribute('eventDate'),
      eventStartTime: dataAttribute('eventStartTime'),
      eventEndTime: dataAttribute('eventEndTime'),
      eventDuration: dataAttribute('eventDuration'),
      eventStartsAt: dataAttribute('eventStartsAt'), // legacy, see above
      eventEndsAt: dataAttribute('eventEndsAt'),     // legacy, see above
      eventTimeZone: dataAttribute('eventTimeZone'),
      eventLocation: dataAttribute('eventLocation'),
      eventDescription: dataAttribute('eventDescription'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html, default_time_zone } — injected by the tiptap controller from the server template
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;

      const dom = document.createElement('div');
      dom.setAttribute('data-event-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Event</div>';

      const field = name => dom.querySelector(`[data-field="${name}"]`);
      const preview = dom.querySelector('[data-preview]');
      const hint = dom.querySelector('[data-preview-hint]');
      const endError = dom.querySelector('[data-end-error]');
      const endPanels = dom.querySelectorAll('[data-end-mode-panel]');
      const endLabel = dom.querySelector('[data-end-label]');
      const endToggle = dom.querySelector('[data-end-mode-toggle]');
      const hoursField = field('duration-hours');
      const minutesField = field('duration-minutes');

      const attrsNow = () => withDateAndTimes(currentNode.attrs);

      // The end is an end time or a duration, one at a time; a saved duration opens in its own mode.
      let endMode = currentNode.attrs.eventDuration ? 'duration' : 'end-time';

      // An end time only counts after the start (the server drops one that isn't).
      const endBeforeStart = () => {
        const { eventStartTime, eventEndTime } = attrsNow();
        return endMode === 'end-time' && Boolean(eventStartTime && eventEndTime) && eventEndTime <= eventStartTime;
      };

      // An event needs a title, a date and a start time.
      const canFinish = () => {
        const { eventTitle, eventDate, eventStartTime } = attrsNow();
        return Boolean((eventTitle || '').trim() && eventDate && eventStartTime) && !endBeforeStart();
      };

      const showEndMode = () => {
        endPanels.forEach(panel => {
          const active = panel.dataset.endModePanel === endMode;
          panel.classList.toggle('hidden', !active);
          if (panel.dataset.endModePanel === 'duration') panel.classList.toggle('flex', active);
        });
        if (endLabel) endLabel.textContent = endMode === 'duration' ? 'Duration (optional)' : 'End time (optional)';
        if (endToggle) endToggle.textContent = endMode === 'duration' ? 'Use end time' : 'Use duration';
      };

      let views = null;

      const syncFromNode = () => {
        const attrs = attrsNow();
        Object.entries(FIELDS).forEach(([name, attr]) => {
          const el = field(name);
          if (!el || el === document.activeElement) return;
          if (name === 'time-zone') {
            // A zone the list doesn't offer gets its own option, so the select never renders blank;
            // an empty one falls back to the creator's zone.
            const zone = attrs[attr] || extension.options.templates?.default_time_zone || '';
            if (zone && ![...el.options].some(option => option.value === zone)) el.add(new Option(zone.replace(/_/g, ' '), zone));
            el.value = zone;
            // Persist the default so the saved block carries a zone (same value next pass, so no loop).
            if (zone && !attrs[attr]) queueMicrotask(() => { if (!currentNode.attrs[attr]) setAttr(attr, zone); });
            return;
          }
          el.value = attrs[attr] ?? '';
        });
        const minutes = parseInt(attrs.eventDuration, 10) || 0;
        if (hoursField && hoursField !== document.activeElement) hoursField.value = minutes ? Math.floor(minutes / 60) : '';
        if (minutesField && minutesField !== document.activeElement) minutesField.value = minutes ? minutes % 60 : '';
        showEndMode();
        endError?.classList.toggle('hidden', !endBeforeStart());
        views?.refresh();
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        hint?.classList.toggle('hidden', Boolean(html));
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml(attrsNow());
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      // Writing any value also moves a legacy start/end onto the date and times, so the two never disagree.
      const setAttrs = changes => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const next = { ...withDateAndTimes(currentNode.attrs), ...changes };
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, next));
      };
      const setAttr = (attr, value) => setAttrs({ [attr]: value });

      Object.entries(FIELDS).forEach(([name, attr]) => {
        field(name)?.addEventListener('input', e => setAttr(attr, e.target.value));
        field(name)?.addEventListener('change', e => setAttr(attr, e.target.value));
      });

      const setDuration = () => {
        const total = (parseInt(hoursField?.value, 10) || 0) * 60 + (parseInt(minutesField?.value, 10) || 0);
        setAttr('eventDuration', total > 0 ? String(total) : '');
      };
      hoursField?.addEventListener('input', setDuration);
      minutesField?.addEventListener('input', setDuration);

      endToggle?.addEventListener('click', () => {
        endMode = endMode === 'duration' ? 'end-time' : 'duration';
        // Only one of the two is kept, so the one just left can't linger in the saved block.
        setAttrs(endMode === 'duration' ? { eventEndTime: '' } : { eventDuration: '' });
        syncFromNode();
      });

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview has a real link; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      views = blockViews(dom, {
        canFinish,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });
      syncFromNode();
      views.setView(initialView({ hasContent: canFinish() }));

      return {
        dom,
        // Typing in the form fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, select, textarea, button')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'event') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertEvent: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export const eventSlashItem = {
  title: 'Event',
  description: 'A date, time and place with an add-to-calendar link',
  icon: '◷',
  group: 'Blocks',
  keywords: ['event', 'calendar', 'date', 'meetup', 'webinar', 'ics'],
  command: ({ editor, range }) => {
    // A new event starts in the creator's own time zone, which the server sends with the editor template.
    const zone = editor.extensionManager.extensions.find(e => e.name === 'event')?.options.templates?.default_time_zone;
    editor.chain().focus().deleteRange(range).insertEvent(zone ? { eventTimeZone: zone } : {}).run();
  },
};
